class WdCustomerReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-wd-reviews-track]");
    this.fadeStart = this.querySelector("[data-wd-reviews-fade-start]");
    this.fadeEnd = this.querySelector("[data-wd-reviews-fade-end]");

    this.querySelectorAll("[data-wd-review-toggle]").forEach((button) => {
      button.addEventListener("click", () => this.toggleReview(button));
    });

    this.querySelectorAll("[data-wd-scroll-to-buy]").forEach((button) => {
      button.addEventListener("click", (event) => this.scrollToBuy(event));
    });

    this.querySelectorAll("[data-wd-review-photo]").forEach((button) => {
      button.addEventListener("click", () => this.openPhotoLightbox(button));
      this.prefetchPhotoSize(button);
    });

    if (this.track) {
      this.track.addEventListener("scroll", () => this.updateFades(), {
        passive: true,
      });
      window.addEventListener("resize", () => this.updateFades());
      this.updateFades();
    }
  }

  toggleReview(button) {
    const card = button.closest("[data-wd-review-card]");
    if (!card) return;

    const body = card.querySelector("[data-wd-review-body]");
    if (!body) return;

    const expanded = card.classList.toggle("is-expanded");
    body.classList.toggle("is-expanded", expanded);
    button.setAttribute("aria-expanded", expanded ? "true" : "false");

    const readMore =
      button.dataset.wdReadMoreLabel || button.textContent.trim();
    const readLess = button.dataset.wdReadLessLabel || readMore;
    button.textContent = expanded ? readLess : readMore;
  }

  scrollToBuy(event) {
    const target = document.querySelector(
      ".wd-pdp__buy, .product-form__submit",
    );
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.focus?.({ preventScroll: true });
  }

  updateFades() {
    if (!this.track || !this.fadeStart || !this.fadeEnd) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    const hasOverflow = maxScroll > 1;

    this.fadeStart.hidden = !hasOverflow || scrollLeft <= 1;
    this.fadeEnd.hidden = !hasOverflow || scrollLeft >= maxScroll - 1;
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;
    if (typeof PhotoSwipeLightbox === "undefined") return null;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG:
        '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),
      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: true,
      returnFocus: true,
      zoom: true,
      initialZoomLevel: "fit",
      secondaryZoomLevel: 1.5,
      maxZoomLevel: 3,
      paddingFn: (viewportSize) => {
        const gutter = Math.min(40, Math.round(viewportSize.x * 0.04));
        return { top: gutter, bottom: gutter, left: gutter, right: gutter };
      },
    });

    lightbox.addFilter("thumbEl", (_thumbEl, data) => data.thumbnailElement);
    lightbox.init();
    this._photoswipe = lightbox;
    return lightbox;
  }

  measureImage(src, image) {
    if (image?.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
      return Promise.resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    }

    return new Promise((resolve) => {
      const probe = new Image();
      probe.onload = () => {
        resolve({
          width: probe.naturalWidth || 1200,
          height: probe.naturalHeight || 1200,
        });
      };
      probe.onerror = () => resolve({ width: 1200, height: 1200 });
      probe.src = src;
    });
  }

  async resolvePhotoItem(button) {
    const image = button.querySelector("img");
    const src = button.dataset.wdFullSrc || image?.currentSrc || image?.src;
    if (!src) return null;

    let width = Number(button.dataset.wdWidth) || 0;
    let height = Number(button.dataset.wdHeight) || 0;

    if (!width || !height) {
      const measured = await this.measureImage(src, image);
      width = measured.width;
      height = measured.height;
      button.dataset.wdWidth = String(width);
      button.dataset.wdHeight = String(height);
    }

    return {
      thumbnailElement: image,
      src,
      msrc: image?.currentSrc || image?.src || src,
      width,
      height,
      alt: image?.alt || "",
      thumbCropped: true,
    };
  }

  prefetchPhotoSize(button) {
    this.resolvePhotoItem(button).catch(() => {});
  }

  async openPhotoLightbox(trigger) {
    const card = trigger.closest("[data-wd-review-card]");
    if (!card) return;

    const triggers = Array.from(
      card.querySelectorAll("[data-wd-review-photo]"),
    );
    const index = Math.max(0, triggers.indexOf(trigger));
    const dataSource = (
      await Promise.all(triggers.map((button) => this.resolvePhotoItem(button)))
    ).filter(Boolean);

    if (!dataSource.length) return;

    const lightbox = this.photoswipe;
    if (!lightbox) return;

    lightbox.loadAndOpen(index, dataSource);
  }
}

if (!customElements.get("wd-customer-reviews")) {
  customElements.define("wd-customer-reviews", WdCustomerReviews);
}
